import {
  AddLeadMutation,
  AssignLeadMutation,
  DismissLeadMutation,
  ProfileLeadMutation,
  useAddLeadMutation,
  useAssignLeadMutation,
  useDismissLeadMutation,
  useProfileLeadMutation,
} from 'graphql/bindings/AdminBindings'
import { ExecutionResult, MutationOptions } from 'react-apollo'
import { useSnackbar } from '@forgeglobal/design-framework'
import { useState } from 'react'

export const useLeadActions = ({ refetchQueries }: { refetchQueries: MutationOptions['refetchQueries'] }) => {
  const { enqueueSnackbar } = useSnackbar()

  const [addLead] = useAddLeadMutation({ refetchQueries, awaitRefetchQueries: true })
  const [dismissLead] = useDismissLeadMutation({ refetchQueries, awaitRefetchQueries: true })
  const [profileLead] = useProfileLeadMutation({ refetchQueries, awaitRefetchQueries: true })
  const [assignLead] = useAssignLeadMutation({ refetchQueries, awaitRefetchQueries: true })

  const [loading, setLoading] = useState(false)

  async function catchAndSetLoading<T>(promise: Promise<ExecutionResult<T>>) {
    return promise
      .catch(e => {
        setLoading(false)
        throw e
      })
      .then((result: ExecutionResult<T> | undefined) => {
        setLoading(false)
        return result
      })
  }

  return {
    loading,
    addLead: async (accountId: string) => {
      setLoading(true)

      const result = await catchAndSetLoading<AddLeadMutation>(addLead({ variables: { accountId } }))
      const account = result?.data?.addLead

      if (account) {
        const accountName = account.name
        enqueueSnackbar(`Added account ${accountName} to leads.`, { variant: 'success' })
      }

      return account
    },
    dismissLead: async (accountId: string) => {
      setLoading(true)

      const result = await catchAndSetLoading<DismissLeadMutation>(dismissLead({ variables: { accountId } }))
      const account = result?.data?.dismissLead

      if (account) {
        const accountName = account.name
        enqueueSnackbar(`Marked account lead ${accountName} as dismissed.`, { variant: 'success' })
      }

      return account
    },
    profileLead: async (accountId: string, qualified: boolean) => {
      setLoading(true)

      const result = await catchAndSetLoading<ProfileLeadMutation>(profileLead({ variables: { accountId, qualified } }))
      const account = result?.data?.profileLead

      if (account) {
        const accountName = account.name
        const status = account.qualified ? 'qualified' : 'not qualified'
        enqueueSnackbar(`Account ${accountName} marked as ${status}.`, { variant: 'success' })
      }

      return account
    },
    assignLead: async (accountId: string, assigneeId: number) => {
      setLoading(true)

      const result = await catchAndSetLoading<AssignLeadMutation>(assignLead({ variables: { accountId, assigneeId } }))
      const account = result?.data?.assignLead

      if (account) {
        const accountName = account.name
        const assigneeName = account.profilingAssignees[account.profilingAssignees.length - 1]?.name
        enqueueSnackbar(`Assigned account ${accountName} to ${assigneeName}.`, { variant: 'success' })
      }

      return account
    },
  }
}
